import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Play } from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import { useTranslation } from 'react-i18next';
import { fetchRemediations, triggerRemediationScan } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useAutoRefresh } from '../contexts/AutoRefreshContext';
import { exportToCSV } from '../lib/export';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Remediation {
  id: string;
  tenant_id: string;
  host_name: string;
  finding_type: string;
  severity: string;
  status: string;
  deploy_channel: string;
  cve_id: string;
  sca_check_id: string;
  auto_approved: boolean;
  ai_cost_cad: number;
  created_at: string;
}

interface PaginatedResponse {
  items: Remediation[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

export default function RemediationsPage() {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [findingType, setFindingType] = useState('');
  const [search, setSearch] = useState('');
  const { selectedTenantId } = useTenant();
  const { subscribe } = useAutoRefresh();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filteredItems = useMemo(() => {
    if (!search || !data?.items) return data?.items || [];
    const q = search.toLowerCase();
    return data.items.filter(
      (r) =>
        r.host_name.toLowerCase().includes(q) ||
        r.cve_id?.toLowerCase().includes(q) ||
        r.finding_type.toLowerCase().includes(q)
    );
  }, [data?.items, search]);

  const handleExport = () => {
    if (!filteredItems.length) return;
    exportToCSV(
      filteredItems.map((r) => ({
        ...r,
        created_at: r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd HH:mm') : '',
      })),
      [
        { key: 'id', header: 'ID' },
        { key: 'host_name', header: 'Hostname' },
        { key: 'finding_type', header: 'Type' },
        { key: 'severity', header: 'Severity' },
        { key: 'status', header: 'Status' },
        { key: 'deploy_channel', header: 'Channel' },
        { key: 'created_at', header: 'Date' },
      ],
      `remediations-${format(new Date(), 'yyyy-MM-dd')}`
    );
    toast.success(t('common.exported', { count: filteredItems.length }));
  };

  const handleTriggerScan = async () => {
    setScanning(true);
    try {
      await triggerRemediationScan();
      toast.success(t('remediation.scanTriggered'));
    } catch {
      toast.error(t('remediation.scanFailed'));
    } finally {
      setScanning(false);
    }
  };

  const loadData = useCallback(() => {
    setLoading(true);
    fetchRemediations({
      page,
      page_size: 20,
      status: status || undefined,
      severity: severity || undefined,
      finding_type: findingType || undefined,
      tenant_id: selectedTenantId || undefined,
    })
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, status, severity, findingType, selectedTenantId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => subscribe(loadData), [subscribe, loadData]);

  const columns: Column<Remediation>[] = [
    {
      key: 'host_name',
      header: t('remediation.hostname'),
      render: (r) => <span className="font-medium">{r.host_name}</span>,
    },
    {
      key: 'finding_type',
      header: t('remediation.findingType'),
      render: (r) => (
        <span className="capitalize text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          {r.finding_type.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      key: 'severity',
      header: t('remediation.severity'),
      render: (r) => (
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${severityColors[r.severity] || ''}`}>
          {r.severity}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('remediation.status'),
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: 'deploy_channel',
      header: t('remediation.channel'),
      render: (r) => r.deploy_channel ? r.deploy_channel.replace(/_/g, ' ') : '-',
    },
    {
      key: 'created_at',
      header: t('remediation.date'),
      render: (r) => r.created_at ? format(new Date(r.created_at), 'MMM d, HH:mm') : '-',
    },
  ];

  const selectClass = "text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className={selectClass}
        >
          <option value="">{t('remediation.allStatuses')}</option>
          <option value="pending">{t('remediation.statusPending')}</option>
          <option value="approved">{t('remediation.statusApproved')}</option>
          <option value="deployed">{t('remediation.statusDeployed')}</option>
          <option value="completed">{t('remediation.statusCompleted')}</option>
          <option value="failed">{t('remediation.statusFailed')}</option>
          <option value="rejected">{t('remediation.statusRejected')}</option>
        </select>
        <select
          value={severity}
          onChange={(e) => { setSeverity(e.target.value); setPage(1); }}
          className={selectClass}
        >
          <option value="">{t('alerts.allSeverities')}</option>
          <option value="critical">{t('alerts.critical')}</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          value={findingType}
          onChange={(e) => { setFindingType(e.target.value); setPage(1); }}
          className={selectClass}
        >
          <option value="">{t('remediation.allTypes')}</option>
          <option value="cve">CVE</option>
          <option value="sca_fail">SCA Fail</option>
          <option value="security_alert">Security Alert</option>
        </select>
        <button
          onClick={handleExport}
          disabled={!filteredItems.length}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
        <button
          onClick={handleTriggerScan}
          disabled={scanning}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 ml-auto"
        >
          <Play className="w-4 h-4" />
          {scanning ? t('remediation.scanning') : t('remediation.triggerScan')}
        </button>
        {data && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredItems.length}/{data.total} {t('common.results')} &middot; {t('common.page')} {data.page}/{data.total_pages}
          </p>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />
      ) : (
        <>
          <DataTable
            data={filteredItems}
            columns={columns}
            keyExtractor={(r) => r.id}
            onRowClick={(r) => navigate(`/remediations/${r.id}`)}
            emptyMessage={t('remediation.noResults')}
          />
          {data && data.total_pages > 1 && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                disabled={page >= data.total_pages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
